import { volumeCapPayload, UNCAPPED_LABEL } from "utilities/volumeCap";
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "components/PageLayout";
import { ArrowLeft, Save, X } from "lucide-react";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useAuth, requireAuth } from "utilities/auth";
import { useToast } from "components/Toast";
import CompanyPickerField from "components/CompanyPickerField";
import LinkPickerField from "components/Tickets/LinkPickerField";


/**
 * DEFINED AT MODULE SCOPE, deliberately.
 *
 * These lived inside MidCreate, which meant every render created a NEW component
 * type — so React unmounted and remounted the entire form subtree on each
 * keystroke. The visible symptom was the Application search losing focus after
 * one character and refiring its request; the cause was that the input it was
 * typing into no longer existed.
 *
 * Both take everything they need as props, so there is no reason for them to be
 * closures.
 */
// One section shape for the whole page. SINGLE COLUMN by decision: a VA keys
// MIDs all day and tabs straight down, so a right rail would only be something
// to look at. Chrome is the page's existing ring-1 card, not the record rail's
// compact header — that treatment belongs in a 320px column, not on a
// full-width entry surface.
const Section = ({ title, description, children }) => (
  <section className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
    <div>
      <h2 className="text-lg font-semibold">{title}</h2>
      {description && (
        <p className="mt-0.5 text-sm text-muted-foreground">{description}</p>
      )}
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
  </section>
);

// `label` is optional: some controls (CompanyPickerField) render their own,
// and wrapping those produced the duplicated "Company / Company" heading.
const Field = ({ label, required, hint, full, children }) => (
  <div className={full ? "md:col-span-2" : undefined}>
    {label && (
      <label className="mb-1 block text-sm font-medium text-foreground">
        {label} {required && <span className="text-destructive">*</span>}
      </label>
    )}
    {children}
    {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
  </div>
);

function MidCreate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const prefilledAppId = searchParams.get("applicationId") || "";
  const { user } = useAuth();
  const { toast } = useToast();

  const [saving, setSaving] = useState(false);
  const [linkedApp, setLinkedApp] = useState(null);
  const [loadingApp, setLoadingApp] = useState(false);
  const [processors, setProcessors] = useState([]);

  const [form, setForm] = useState({
    applicationId: prefilledAppId,
    companyId: "",
    companyName: "",
    mid: "",
    descriptor: "",
    mcc: "",
    caid: "",
    name: "",
    processorName: "",
    processorId: "",
    dbaId: "",
    acquiringBank: "",
    bin: "",
    currencies: "USD",
    notes: "",
    tags: "",
    // Volume cap (SOUND-512/657). Uncapped is now a STATE on the MID, not the
    // absence of a number — a blank cap is $0. Default to capped so creating an
    // uncapped MID stays a deliberate choice.
    monthlyVolumeCap: "",
    isUncapped: false,
    // SOUND-642: neither field was capturable anywhere in the product — not on
    // create, not on edit — which is why effectiveDate is null on all 261 MIDs
    // and the portfolio cannot answer "when did this MID go live".
    // POST /mids has always accepted both.
    effectiveDate: "",
    chargebackThreshold: "",
    // Sound owns this MID (sourced it / gets the residual / owns the bank
    // relationship) vs. one we only manage or monitor. Default on — the norm.
    isSoundOwned: true,
    reserveType: "none",
    reservePercentage: "",
    reserveHoldPeriod: "",
    reserveCap: "",
  });

  // Fetch processors list (only needed when no linked application)
  useEffect(() => {
    if (prefilledAppId) return; // skip if we have a linked app — processor comes from it
    const fetchProcessors = async () => {
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.getProcessors,
          { id: user.id, accessToken: user.accessToken },
          user.companyId
        );
        setProcessors(res.data?.data || []);
      } catch {
        // non-critical
      }
    };
    fetchProcessors();
  }, [user, prefilledAppId]);

  // Fetch linked application context if applicationId present
  useEffect(() => {
    if (!form.applicationId) {
      setLinkedApp(null);
      return;
    }

    const fetchApp = async () => {
      setLoadingApp(true);
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.getMidApplicationById(form.applicationId),
          { id: user.id, accessToken: user.accessToken },
          user.companyId
        );
        const app = res.data?.data || null;
        setLinkedApp(app);
        // Auto-populate processor from linked application
        // Prefill what the application already knows. `dbaId` lands in the DBA
        // field rather than being inferred later — the application is the
        // record that actually knows which brand the MID is for.
        setForm((prev) => ({
          ...prev,
          ...(app?.processorName ? { processorName: app.processorName } : {}),
          ...(app?.dbaId ? { dbaId: app.dbaId } : {}),
        }));
      } catch {
        setLinkedApp(null);
      } finally {
        setLoadingApp(false);
      }
    };

    fetchApp();
  }, [form.applicationId, user]);

  // SOUND-649: a MID may be linked at creation to the DBA it trades under.
  // OPTIONAL by decision — many MIDs are keyed before the brand is known, and
  // some companies' DBAs are still unnamed — so this never blocks the form.
  // Read off the company's registration: that response already assembles dbas
  // from the collection, so no new endpoint is needed.
  const [dbaOptions, setDbaOptions] = useState([]);
  const dbaCompanyId = linkedApp?.companyId || form.companyId;

  useEffect(() => {
    if (!dbaCompanyId || !user) {
      setDbaOptions([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await makeApiRequest(
          "get",
          apiRoutes.getApplication(dbaCompanyId),
          { id: user.id, accessToken: user.accessToken },
          user.companyId
        );
        const dbas = res?.data?.dbas || [];
        if (!cancelled) setDbaOptions(dbas.filter((d) => String(d.dbaName || "").trim()));
      } catch {
        if (!cancelled) setDbaOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dbaCompanyId, user]);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Client-side validation
    if (!form.applicationId.trim() && !form.companyId) {
      toast({ status: "warning", title: "Select a company or link an application" });
      return;
    }
    if (!form.mid.trim()) {
      toast({ status: "warning", title: "MID is required" });
      return;
    }
    if (!form.descriptor.trim()) {
      toast({ status: "warning", title: "Descriptor is required" });
      return;
    }
    if (!/^\d{4}$/.test(form.mcc)) {
      toast({ status: "warning", title: "MCC must be exactly 4 digits" });
      return;
    }
    if (!form.caid.trim()) {
      toast({ status: "warning", title: "CAID is required" });
      return;
    }
    if (!form.processorId && !form.processorName.trim()) {
      toast({ status: "warning", title: "Processor is required" });
      return;
    }

    setSaving(true);
    try {
      const payload = {
        applicationId: form.applicationId.trim() || undefined,
        companyId: !form.applicationId.trim() ? form.companyId : undefined,
        mid: form.mid.trim(),
        descriptor: form.descriptor.trim(),
        mcc: form.mcc.trim(),
        caid: form.caid.trim(),
        name: form.name.trim() || undefined,
        // The IDENTIFIER is the input; processorName is a cache the API fills
        // from the registry. Still sent for the application-linked path, where
        // the processor comes from the application rather than the picker.
        processorId: form.processorId || undefined,
        processorName: form.processorName.trim() || undefined,
        // Becomes a mid -> dba LINK server-side, not a stamped field.
        dbaId: form.dbaId || undefined,
        acquiringBank: form.acquiringBank.trim() || undefined,
        bin: form.bin.trim() || undefined,
        currencies: form.currencies
          ? form.currencies.split(",").map((c) => c.trim()).filter(Boolean)
          : ["USD"],
        notes: form.notes.trim() || undefined,
        tags: form.tags
          ? form.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : undefined,
        effectiveDate: form.effectiveDate || null,
        chargebackThreshold:
          form.chargebackThreshold === "" ? null : Number(form.chargebackThreshold),
        // SOUND-657: the two cap fields are mutually exclusive and settled in
        // one place, so no surface can send a MID that is both uncapped and
        // capped at $50k. The server reconciles them again.
        ...volumeCapPayload(form),
        reserve: form.reserveType !== "none"
          ? {
              type: form.reserveType,
              percentage: form.reservePercentage ? Number(form.reservePercentage) : undefined,
              holdPeriod: form.reserveHoldPeriod ? Number(form.reserveHoldPeriod) : undefined,
              cap: form.reserveCap ? Number(form.reserveCap) : undefined,
            }
          : undefined,
        isSoundOwned: form.isSoundOwned,
      };

      const res = await makeApiRequest(
        "post",
        apiRoutes.createMid,
        { id: user.id, accessToken: user.accessToken },
        user.companyId,
        payload
      );

      const created = res.data?.data;
      toast({
        status: "success",
        title: "MID created",
        description: `MID ${created?.mid || ""} added to portfolio.`,
        action: {
          label: "View MID",
          onClick: () => navigate(`/mids/${created?.midId}`),
        },
      });

      navigate(`/mids/${created?.midId}`);
    } catch (err) {
      console.error("[MidCreate] Failed to create MID", err);
      toast({
        status: "destructive",
        title: "Failed to create MID",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSaving(false);
    }
  };



  // Selecting an application means the MID is being created FROM it, so the
  // facts it already knows stop being editable. Clearing hands the form back.
  const clearApplication = () => {
    setForm((prev) => ({
      ...prev,
      applicationId: "",
      processorName: "",
      processorId: "",
      // Came from the application, so it leaves with it.
      dbaId: "",
    }));
    setLinkedApp(null);
  };

  return (
    <div>
      <PageHeader
        title="Create MID"
        subtitle="Add a MID to the portfolio — from an approved application, or keyed directly."
        actions={
          <div className="flex gap-2">
            <button className="btn btn-ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-4 w-4" />
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              <Save className="h-4 w-4" />
              {saving ? "Creating…" : "Create MID"}
            </button>
          </div>
        }
      />

      <div className="container py-6 space-y-6">
        {/* 1. ATTACH — all three relationships in ONE place. They were spread
            across a company card, a UUID paste field buried in MID data, and a
            dropdown: three surfaces answering one question. */}
        <Section
          title="Attach"
          description="Who this MID belongs to. Linking an application fills in what it already knows."
        >
          <Field>
            {linkedApp ? (
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Company <span className="text-destructive">*</span>
                </label>
                <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2">
                  <span className="text-sm text-foreground">
                    {/* clientName, NOT companyName. The stored companyName on a
                        MID application is whatever was passed at create time —
                        frequently the DBA brand — and the route enriches the
                        real company name as clientName. Using companyName here
                        put a DBA in the Company row. */}
                    {linkedApp.clientName ||
                      linkedApp.companyName ||
                      form.companyName ||
                      linkedApp.companyId}
                  </span>
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    from application
                  </span>
                </div>
              </div>
            ) : (
              <CompanyPickerField
                value={form.companyId}
                valueLabel={form.companyName}
                // onChange({ id, name }) — ONE object. Destructuring it as two
                // positional args put the object itself into companyId, which
                // React then tried to render as a child.
                onChange={(picked) =>
                  setForm((prev) => ({
                    ...prev,
                    companyId: picked?.id || "",
                    companyName: picked?.name || "",
                    // A different company invalidates the DBA choice.
                    dbaId: "",
                  }))
                }
              />
            )}
          </Field>

          {/* Right column: the search is the widest control here and reads
              better beside the two selects than stacked under them. */}
          <Field label="Application" hint="Optional. Search by merchant or DBA — no ID to paste.">
            {form.applicationId ? (
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
                <div className="min-w-0">
                  <div className="truncate text-sm text-foreground">
                    {linkedApp
                      ? `${linkedApp.processorName || "Application"}${linkedApp.dbaName ? ` · ${linkedApp.dbaName}` : ""}`
                      : loadingApp
                      ? "Loading…"
                      : form.applicationId}
                  </div>
                  {linkedApp?.currentStatus && (
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                      {linkedApp.currentStatus}
                    </div>
                  )}
                </div>
                {!prefilledAppId && (
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-destructive"
                    onClick={clearApplication}
                    title="Detach application"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            ) : (
              <LinkPickerField
                entityType="application"
                companyId={form.companyId || undefined}
                placeholder={form.companyId ? "Search applications…" : "Search applications (any company)…"}
                onSelect={({ entityId }) => updateField("applicationId", entityId)}
              />
            )}
          </Field>

          <Field
            label="DBA"
            hint={
              linkedApp && form.dbaId
                ? "From the linked application. Detach it to choose a different DBA."
                : "Optional — creates a link, not a copied field. Attachable later from the MID record."
            }
          >
            {/* Locked while an application is attached, for the same reason
                Company is: the application already knows which brand the MID is
                for, so editing it here would let the two disagree.
                NOT locked when the application carries no dbaId — 21 of 160 do
                not — because locking an empty field would remove the only way
                to attach one. */}
            {linkedApp && form.dbaId ? (
              <div className="flex items-center justify-between rounded-md border border-border bg-muted/40 px-3 py-2">
                <span className="text-sm text-foreground">
                  {dbaOptions.find((d) => d.dbaId === form.dbaId)?.dbaName ||
                    linkedApp.dbaName ||
                    form.dbaId}
                </span>
                <span className="text-xs uppercase tracking-wide text-muted-foreground">
                  from application
                </span>
              </div>
            ) : (
              <select
                className="select select-bordered w-full"
                value={form.dbaId}
                onChange={(e) => updateField("dbaId", e.target.value)}
                disabled={!dbaCompanyId}
              >
                <option value="">
                  {!dbaCompanyId
                    ? "Choose a company first"
                    : dbaOptions.length === 0
                    ? "No named DBAs on this company"
                    : "Not linked"}
                </option>
                {dbaOptions.map((d) => (
                  <option key={d.dbaId} value={d.dbaId}>
                    {d.dbaName}
                  </option>
                ))}
              </select>
            )}
          </Field>
        </Section>

        {/* 2. WHAT THE MID IS */}
        <Section title="MID identity">
          <Field label="MID number" required>
            <input className="input input-bordered w-full" value={form.mid}
              onChange={(e) => updateField("mid", e.target.value)} placeholder="MID number from processor" />
          </Field>
          <Field label="Descriptor" required>
            <input className="input input-bordered w-full" value={form.descriptor}
              onChange={(e) => updateField("descriptor", e.target.value)} placeholder="Billing descriptor" />
          </Field>
          <Field label="Name" hint="How this MID is referred to internally.">
            <input className="input input-bordered w-full" value={form.name}
              onChange={(e) => updateField("name", e.target.value)} placeholder="Short name" />
          </Field>
          <Field label="CAID" required>
            <input className="input input-bordered w-full" value={form.caid}
              onChange={(e) => updateField("caid", e.target.value)} placeholder="Card Acceptor ID" />
          </Field>
          <Field label="MCC" required>
            <input className="input input-bordered w-full" value={form.mcc}
              onChange={(e) => updateField("mcc", e.target.value)} placeholder="4-digit MCC" />
          </Field>
          <Field label="BIN">
            <input className="input input-bordered w-full" value={form.bin}
              onChange={(e) => updateField("bin", e.target.value)} placeholder="Optional" />
          </Field>
        </Section>

        {/* 3. PROCESSOR AND TERMS */}
        <Section title="Processor & terms">
          <Field label="Processor" required>
            {linkedApp ? (
              <input className="input input-bordered w-full bg-muted" value={form.processorName} disabled />
            ) : (
              <select
                className="select select-bordered w-full"
                value={form.processorId}
                onChange={(e) => {
                  // Send the IDENTIFIER. A label the server reverse-resolves is
                  // what allowed a MID to end up with a processor nothing points at.
                  const p = processors.find((x) => x.processorId === e.target.value);
                  setForm((prev) => ({ ...prev, processorId: e.target.value, processorName: p?.name || "" }));
                }}
              >
                <option value="">Select a processor</option>
                {processors.map((p) => (
                  <option key={p.processorId} value={p.processorId}>{p.name}</option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Acquiring bank">
            <input className="input input-bordered w-full" value={form.acquiringBank}
              onChange={(e) => updateField("acquiringBank", e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="Effective date" hint="When the MID went live.">
            <input type="date" className="input input-bordered w-full" value={form.effectiveDate}
              onChange={(e) => updateField("effectiveDate", e.target.value)} />
          </Field>
          <Field label="Chargeback threshold (%)">
            <input className="input input-bordered w-full" value={form.chargebackThreshold}
              onChange={(e) => updateField("chargebackThreshold", e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="Monthly volume cap ($)" hint="Blank is $0. Tick Uncapped for no limit.">
            <div className="flex items-center gap-3">
              <input className="input input-bordered w-full" value={form.monthlyVolumeCap}
                onChange={(e) => updateField("monthlyVolumeCap", e.target.value)}
                disabled={form.isUncapped} placeholder="50000" />
              <label className="flex items-center gap-1.5 whitespace-nowrap text-[13px] text-muted-foreground">
                <input type="checkbox" checked={form.isUncapped}
                  onChange={(e) => updateField("isUncapped", e.target.checked)} />
                {UNCAPPED_LABEL}
              </label>
            </div>
          </Field>
          <Field label="Currencies" hint="Comma separated.">
            <input className="input input-bordered w-full" value={form.currencies}
              onChange={(e) => updateField("currencies", e.target.value)} placeholder="USD" />
          </Field>
        </Section>

        {/* RESERVE — dependent fields, so it stays its own section rather than
            being folded into Processor & terms. Percentage / hold / cap only
            exist once a type is chosen; showing them greyed under "None" is
            three inputs of noise on a form used all day. */}
        <Section title="Reserve">
          <Field label="Type" full={form.reserveType === "none"}>
            <select
              className="select select-bordered w-full"
              value={form.reserveType}
              onChange={(e) => updateField("reserveType", e.target.value)}
            >
              <option value="none">None</option>
              <option value="rolling">Rolling</option>
              <option value="upfront">Upfront</option>
            </select>
          </Field>
          {form.reserveType !== "none" && (
            <>
              <Field label="Percentage">
                <input type="number" className="input input-bordered w-full"
                  value={form.reservePercentage}
                  onChange={(e) => updateField("reservePercentage", e.target.value)}
                  placeholder="e.g. 10" />
              </Field>
              <Field label="Hold period (days)">
                <input type="number" className="input input-bordered w-full"
                  value={form.reserveHoldPeriod}
                  onChange={(e) => updateField("reserveHoldPeriod", e.target.value)}
                  placeholder="e.g. 180" />
              </Field>
              <Field label="Cap ($)">
                <input type="number" className="input input-bordered w-full"
                  value={form.reserveCap}
                  onChange={(e) => updateField("reserveCap", e.target.value)}
                  placeholder="Max reserve amount" />
              </Field>
            </>
          )}
        </Section>

        {/* 4. OWNERSHIP AND META */}
        <Section title="Ownership & notes">
          <Field label="Ownership" full>
            <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
              <input type="checkbox" checked={form.isSoundOwned}
                onChange={(e) => updateField("isSoundOwned", e.target.checked)} />
              Sound owns this MID (rather than only managing or monitoring it)
            </label>
          </Field>
          <Field label="Tags" hint="Comma separated." full>
            <input className="input input-bordered w-full" value={form.tags}
              onChange={(e) => updateField("tags", e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="Notes" full>
            <textarea className="textarea textarea-bordered w-full" rows={3} value={form.notes}
              onChange={(e) => updateField("notes", e.target.value)} placeholder="Optional" />
          </Field>
        </Section>
      </div>
    </div>
  );
}

export default requireAuth(MidCreate);
